// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/kiosk-config.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * Shared kiosk configuration model.
 *
 * The superuser (renderer/operator) owns a `KioskConfig`: how many kiosk
 * stations exist, which mode they run in, the idle-reset dataset/timeout, and a
 * stable identity->slice assignment table. The config is broadcast to kiosk
 * clients (see the `KIOSK_CONFIG_*` flags in `flags.ts`), and the server
 * validates a kiosk's persistent ID against it to decide the kiosk's slice and
 * locked state.
 *
 * `KioskMode` here is the set of *configurable* modes. `game` exists as a
 * separate runtime mode (trivia) and is intentionally not a config value.
 *
 * Ported from the pre-v2 `Kiosk` branch and adapted to strict TypeScript
 * (`noUncheckedIndexedAccess`).
 */

export type KioskCount = 1 | 2 | 3 | 4;

export type KioskMode = 'exploration' | 'presentation' | 'cooperative' | 'autoplay';

export interface KioskAssignment {
  id: string;
  label: string;
  sliceIndex: number;
  enabled: boolean;
}

/** How the cooperative kiosk game runs (docs/KIOSK_COOP_GAME_DESIGN.md §8). */
export interface KioskGameSettings {
  rounds: 3 | 5;
  /** Seconds to plan each decade; null is relaxed (wait for everyone). */
  planSeconds: number | null;
  /** Start after the lobby countdown; false waits for the operator. */
  autoStart: boolean;
  /** Offer visitors "Just explore" beside the game. */
  showExplore: boolean;
  /** The kiosk chatbot advises during a game (off: the chat pauses until the game ends). */
  advisor: boolean;
}

export const KIOSK_GAME_PLAN_SECONDS = [45, 60, 90] as const;

export const DEFAULT_KIOSK_GAME_SETTINGS: KioskGameSettings = {
  rounds: 5,
  planSeconds: 60,
  autoStart: true,
  showExplore: true,
  advisor: true
};

export function normalizeKioskGameSettings(value: unknown): KioskGameSettings {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Partial<Record<keyof KioskGameSettings, unknown>>;
  const plan = raw.planSeconds === null ? null : Number(raw.planSeconds);
  return {
    rounds: raw.rounds === 3 || raw.rounds === '3' ? 3 : 5,
    planSeconds: plan === null ? null : (KIOSK_GAME_PLAN_SECONDS as readonly number[]).includes(plan) ? plan : DEFAULT_KIOSK_GAME_SETTINGS.planSeconds,
    autoStart: raw.autoStart !== false,
    showExplore: raw.showExplore !== false,
    advisor: raw.advisor !== false
  };
}

/** The kiosk games the Games mode can run (docs/KIOSK_GAMES_DESIGN.md §3). */
export const KIOSK_GAME_IDS = ['team-earth', 'wayra-adventures', 'geo'] as const;
export type KioskGameId = typeof KIOSK_GAME_IDS[number];

export function isKioskGameId(value: unknown): value is KioskGameId {
  return typeof value === 'string' && (KIOSK_GAME_IDS as readonly string[]).includes(value);
}

/** Wayra's home: the venue, where every game ends (docs/KIOSK_GAMES_DESIGN.md §5.11). */
export interface WayraHome {
  name: string;
  lat: number;
  lon: number;
}

/** How "Wayra's Adventures" runs (docs/KIOSK_GAMES_DESIGN.md §5.11). */
export interface WayraGameSettings {
  /** Stops before home. */
  stops: 3 | 5;
  /** Seconds between clues; null is "on request" (every present kiosk taps Next clue). */
  clueSeconds: number | null;
  /** Candidate places per stop. */
  candidates: 4 | 6;
  timeTravel: 'off' | 'future' | 'past-and-future';
  /** The climate scenario of the future hop. */
  futurePath: 'low' | 'middle' | 'high';
  /** The venue; null lets the last stop be Wayra's home. */
  home: WayraHome | null;
  /** Para, Wayra's always-hungry brother, who eats the treats a slow team leaves. */
  para: boolean;
  /**
   * New places for each game from the AI, checked on OpenStreetMap; the built-in
   * places when the AI is off, unreachable or out of quota.
   */
  aiPlaces: boolean;
  autoStart: boolean;
  showExplore: boolean;
  advisor: boolean;
}

export const WAYRA_CLUE_SECONDS = [15, 20, 30] as const;

export const DEFAULT_WAYRA_GAME_SETTINGS: WayraGameSettings = {
  stops: 5,
  clueSeconds: 20,
  candidates: 6,
  timeTravel: 'past-and-future',
  futurePath: 'middle',
  home: null,
  para: true,
  aiPlaces: true,
  autoStart: true,
  showExplore: true,
  advisor: true
};

function normalizeWayraHome(value: unknown): WayraHome | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  const lat = Number(raw.lat);
  const lon = Number(raw.lon);
  const name = typeof raw.name === 'string' ? raw.name.trim().slice(0, 60) : '';
  if (!name || !Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { name, lat: Math.round(lat * 10000) / 10000, lon: Math.round(lon * 10000) / 10000 };
}

export function normalizeWayraGameSettings(value: unknown): WayraGameSettings {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Partial<Record<keyof WayraGameSettings, unknown>>;
  const d = DEFAULT_WAYRA_GAME_SETTINGS;
  const clue = raw.clueSeconds === null ? null : Number(raw.clueSeconds);
  return {
    stops: raw.stops === 3 || raw.stops === '3' ? 3 : 5,
    clueSeconds: clue === null ? null : (WAYRA_CLUE_SECONDS as readonly number[]).includes(clue) ? clue : d.clueSeconds,
    candidates: raw.candidates === 4 || raw.candidates === '4' ? 4 : 6,
    timeTravel: raw.timeTravel === 'off' || raw.timeTravel === 'future' ? raw.timeTravel : 'past-and-future',
    futurePath: raw.futurePath === 'low' || raw.futurePath === 'high' ? raw.futurePath : 'middle',
    home: normalizeWayraHome(raw.home),
    para: raw.para !== false,
    aiPlaces: raw.aiPlaces !== false,
    autoStart: raw.autoStart !== false,
    showExplore: raw.showExplore !== false,
    advisor: raw.advisor !== false
  };
}

/** How "Maypi: Where on Earth?" runs (docs/KIOSK_GEO_GAME_DESIGN.md §8). */
export interface GeoGameSettings {
  sessionMinutes: 5 | 8 | 10;
  answerSeconds: 20 | 30 | 45;
  /** The chance a question is a photo; 1 = photos only. */
  photoShare: 0.6 | 0.8 | 1;
  /** 2 keeps the hardest photos out (young groups). */
  photoDifficultyMax: 2 | 3;
  aiQuestions: 'on' | 'paused' | 'review';
  aiParagraphs: boolean;
  sphereLeaderboard: boolean;
}

export const DEFAULT_GEO_GAME_SETTINGS: GeoGameSettings = {
  sessionMinutes: 10,
  answerSeconds: 30,
  photoShare: 0.6,
  photoDifficultyMax: 3,
  aiQuestions: 'on',
  aiParagraphs: true,
  sphereLeaderboard: true
};

const oneOf = <T>(value: unknown, allowed: readonly T[], fallback: T): T => {
  const number = typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)) ? Number(value) : value;
  return allowed.includes(number as T) ? (number as T) : allowed.includes(value as T) ? (value as T) : fallback;
};

export function normalizeGeoGameSettings(value: unknown): GeoGameSettings {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Partial<Record<keyof GeoGameSettings, unknown>>;
  const d = DEFAULT_GEO_GAME_SETTINGS;
  return {
    sessionMinutes: oneOf(raw.sessionMinutes, [5, 8, 10] as const, d.sessionMinutes),
    answerSeconds: oneOf(raw.answerSeconds, [20, 30, 45] as const, d.answerSeconds),
    photoShare: oneOf(raw.photoShare, [0.6, 0.8, 1] as const, d.photoShare),
    photoDifficultyMax: oneOf(raw.photoDifficultyMax, [2, 3] as const, d.photoDifficultyMax),
    aiQuestions: oneOf(raw.aiQuestions, ['on', 'paused', 'review'] as const, d.aiQuestions),
    aiParagraphs: typeof raw.aiParagraphs === 'boolean' ? raw.aiParagraphs : d.aiParagraphs,
    sphereLeaderboard: typeof raw.sphereLeaderboard === 'boolean' ? raw.sphereLeaderboard : d.sphereLeaderboard
  };
}

/** The Games mode's settings: which game the next lobby opens, and each game's own settings. */
export interface KioskGamesConfig {
  selected: KioskGameId;
  teamEarth: KioskGameSettings;
  wayra: WayraGameSettings;
  geo: GeoGameSettings;
}

/**
 * The games block from a config. An older config carries only `game`
 * (Team Earth's settings, before the Games panel); it becomes `teamEarth`.
 */
export function normalizeKioskGamesConfig(games: unknown, legacyGame?: unknown): KioskGamesConfig {
  const raw = (typeof games === 'object' && games !== null ? games : {}) as Partial<Record<keyof KioskGamesConfig, unknown>>;
  return {
    selected: isKioskGameId(raw.selected) ? raw.selected : 'team-earth',
    teamEarth: normalizeKioskGameSettings(raw.teamEarth ?? legacyGame),
    wayra: normalizeWayraGameSettings(raw.wayra),
    geo: normalizeGeoGameSettings(raw.geo)
  };
}

/** The settings every game shares: the operator's start, "Just explore" and the advisor. */
export function selectedGameCommon(games: KioskGamesConfig): { autoStart: boolean; showExplore: boolean; advisor: boolean } {
  // Maypi has no lobby, no "Just explore" and no advisor (a player could describe a photo and ask where it is).
  if (games.selected === 'geo') return { autoStart: true, showExplore: false, advisor: false };
  const s = games.selected === 'wayra-adventures' ? games.wayra : games.teamEarth;
  return { autoStart: s.autoStart, showExplore: s.showExplore, advisor: s.advisor };
}

export interface KioskConfig {
  enabled: boolean;
  count: KioskCount;
  mode: KioskMode;
  defaultDatasetId: string | null;
  idleTimeoutSeconds: number;
  kiosks: KioskAssignment[];
  /**
   * Per-install setup PIN gating the kiosk setup modal + operator advanced
   * settings. Generated once by the operator settings UI (empty until then),
   * carried in the broadcast config so served kiosks can validate locally.
   */
  setupPin: string;
  /** The Games mode (stored as 'cooperative'): the selected game and each game's settings. */
  games: KioskGamesConfig;
}

export const KIOSK_MIN_IDLE_TIMEOUT_SECONDS = 10;
export const KIOSK_MAX_IDLE_TIMEOUT_SECONDS = 86400;
export const KIOSK_DEFAULT_IDLE_TIMEOUT_SECONDS = 120;
export const KIOSK_MAX_COUNT = 4;

export const KIOSK_MODES: readonly KioskMode[] = [
  'exploration',
  'presentation',
  'cooperative',
  'autoplay'
] as const;

export function normalizeKioskCount(value: unknown): KioskCount {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 1) return 1;
  if (numericValue === 2) return 2;
  if (numericValue === 3) return 3;
  return 4;
}

export function isKioskMode(value: unknown): value is KioskMode {
  return typeof value === 'string' && KIOSK_MODES.includes(value as KioskMode);
}

/**
 * Normalize a raw kiosk ID to the canonical form used for assignment matching.
 * MUST stay identical everywhere it is applied (client identity, settings
 * editor, server assignment) because matching is by exact normalized equality.
 */
export function normalizeKioskId(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export function createDefaultKioskAssignments(count: KioskCount = 4): KioskAssignment[] {
  return Array.from({ length: KIOSK_MAX_COUNT }, (_unused, index) => ({
    id: `kiosk-${index + 1}`,
    label: `Kiosk ${index + 1}`,
    sliceIndex: index,
    enabled: index < count
  }));
}

export const DEFAULT_KIOSK_CONFIG: KioskConfig = {
  enabled: false,
  count: 4,
  mode: 'exploration',
  defaultDatasetId: null,
  idleTimeoutSeconds: KIOSK_DEFAULT_IDLE_TIMEOUT_SECONDS,
  kiosks: createDefaultKioskAssignments(4),
  setupPin: '',
  games: { selected: 'team-earth', teamEarth: { ...DEFAULT_KIOSK_GAME_SETTINGS }, wayra: { ...DEFAULT_WAYRA_GAME_SETTINGS }, geo: { ...DEFAULT_GEO_GAME_SETTINGS } }
};

/** A random 4-6 digit setup PIN. Never called inside normalizeKioskConfig
 * (which must stay pure/deterministic) — the settings UI calls it once. */
export function generateKioskSetupPin(): string {
  // 6 digits, zero-padded. Uses Math.random — this is app UI code, not a pure
  // normalizer, and the PIN is a low-security physical-access gate.
  return String(Math.floor(Math.random() * 1_000_000)).padStart(6, '0');
}

/** Coerce an entered/configured PIN to digits-only, max 8. */
export function normalizeKioskSetupPin(value: unknown): string {
  return String(value ?? '').replace(/\D+/g, '').slice(0, 8);
}

export function normalizeIdleTimeoutSeconds(value: unknown): number {
  const requested = Number(value ?? KIOSK_DEFAULT_IDLE_TIMEOUT_SECONDS);
  const resolved = Number.isFinite(requested) ? requested : KIOSK_DEFAULT_IDLE_TIMEOUT_SECONDS;
  return Math.min(
    KIOSK_MAX_IDLE_TIMEOUT_SECONDS,
    Math.max(KIOSK_MIN_IDLE_TIMEOUT_SECONDS, Math.round(resolved))
  );
}

/**
 * Produce a fully-populated, invariant-satisfying config from a partial input.
 *
 * Invariants:
 * - `kiosks` is ALWAYS length {@link KIOSK_MAX_COUNT}; reducing `count` disables
 *   the higher-index kiosks but preserves their id/label.
 * - each `sliceIndex` equals its array index.
 * - an assignment is `enabled` only if its index < `count` AND it was not
 *   explicitly disabled.
 */
export function normalizeKioskConfig(config: (Partial<KioskConfig> & { game?: unknown }) | null | undefined): KioskConfig {
  const count = normalizeKioskCount(config?.count ?? DEFAULT_KIOSK_CONFIG.count);
  const mode = isKioskMode(config?.mode) ? config.mode : DEFAULT_KIOSK_CONFIG.mode;
  const idleTimeoutSeconds = normalizeIdleTimeoutSeconds(
    config?.idleTimeoutSeconds ?? DEFAULT_KIOSK_CONFIG.idleTimeoutSeconds
  );
  const defaults = createDefaultKioskAssignments(count);
  const configuredKiosks = Array.isArray(config?.kiosks) ? config.kiosks : [];

  const kiosks = defaults.map((defaultAssignment, index) => {
    const configured = configuredKiosks[index];
    const id = normalizeKioskId(configured?.id) || defaultAssignment.id;
    const label =
      typeof configured?.label === 'string' && configured.label.trim().length > 0
        ? configured.label.trim().slice(0, 80)
        : defaultAssignment.label;
    return {
      id,
      label,
      sliceIndex: index,
      enabled: index < count && configured?.enabled !== false
    };
  });

  return {
    enabled: config?.enabled === true,
    count,
    mode,
    defaultDatasetId: typeof config?.defaultDatasetId === 'string' ? config.defaultDatasetId : null,
    idleTimeoutSeconds,
    kiosks,
    setupPin: normalizeKioskSetupPin(config?.setupPin),
    games: normalizeKioskGamesConfig(config?.games, config?.game)
  };
}
